import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton } from './button/Button';

import '../pages/error-pages/errorPages.css';

/**
 * Error boundary that catches dynamic-import (chunk load) failures and shows
 * a styled error page with a manual reload button.
 *
 * Handles two distinct failure modes:
 *   - 'network'  — the server is unreachable (e.g. VPN down). Shown when
 *                  lazyWithRetry's reachability check fails before reload.
 *   - 'chunk'    — a chunk load failed but the server is reachable (e.g.
 *                  stale deploy, transient blip, CSS preload error).
 *
 * Non-chunk errors are re-thrown so they propagate to parent error boundaries.
 */
class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorType: null };
    }

    static getDerivedStateFromError(error) {
        const msg = error?.message || '';

        if (msg.includes('Network unreachable')) {
            return { hasError: true, errorType: 'network' };
        }

        const isChunkError =
            msg.includes('Failed to fetch dynamically imported module') ||
            msg.includes('dynamically imported module') ||
            msg.includes('Importing a module script failed') ||
            msg.includes('Unable to preload CSS') ||
            msg.includes('Unable to preload module') ||
            msg.includes('Import timeout');

        if (isChunkError) {
            return { hasError: true, errorType: 'chunk' };
        }
        // Not a chunk error — re-throw so other boundaries can handle it
        throw error;
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            if (this.state.errorType === 'network') {
                return (
                    <div className="error-page">
                        <div className="error-card">
                            <div className="error-icon error-icon--error" aria-hidden="true">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </div>
                            <h1>Can't reach the server</h1>
                            <p>Please check your VPN or WiFi connection.</p>
                            <p className="error-subtle">
                                The server didn't respond. Make sure you're connected to the office
                                network or VPN, then try again.
                            </p>
                            <div className="error-actions">
                                <PrimaryButton onClick={this.handleReload}>Try Again</PrimaryButton>
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div className="error-page">
                    <div className="error-card">
                        <div className="error-icon" aria-hidden="true">
                            <FontAwesomeIcon icon={faArrowsRotate} />
                        </div>
                        <h1>Update Available</h1>
                        <p>A new version of the app has been deployed.</p>
                        <p className="error-subtle">Please reload the page to continue.</p>
                        <div className="error-actions">
                            <PrimaryButton onClick={this.handleReload}>Reload Page</PrimaryButton>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ChunkErrorBoundary;
